import {
  CreditCardIcon,
  LandmarkIcon,
  PlugIcon,
  ReceiptIcon,
  SettingsIcon,
  UsersIcon,
  WalletIcon,
} from 'lucide-react'
import { type ReactNode, useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { SettingsLayout } from '@/components/settings/settings-layout.organism'
import type {
  SettingsNavGroup,
  SettingsNavItem,
} from '@/components/settings/settings-nav-item.interface'
import { orgRoutes, settingsRoutes } from '@/routes/org-routes.const'

import { BalanceSettingsView } from './balance-settings.view'
import { ConnectorsSettingsView } from './connectors-settings.view'
import { GeneralSettingsView } from './general-settings.view'
import { InvoicesSettingsView } from './invoices-settings.view'
import { MembersSettingsView } from './members-settings.view'
import {
  ORG_SETTINGS_DEFAULT_VIEW,
  ORG_SETTINGS_URL_PATH_TO_VIEW,
  ORG_SETTINGS_VIEW_DESCRIPTIONS,
  ORG_SETTINGS_VIEW_TITLES,
  ORG_SETTINGS_VIEW_TO_URL_PATH,
  type OrgSettingsViewType,
} from './org-settings-view.type'
import { PaymentMethodsSettingsView } from './payment-methods-settings.view'
import { SubscriptionSettingsView } from './subscription-settings.view'

const ORG_SETTINGS_NAV_GROUPS: SettingsNavGroup[] = [
  {
    id: 'organization',
    label: 'Organization',
    itemList: [
      { id: 'general', label: 'General', href: settingsRoutes.general, icon: SettingsIcon },
      { id: 'members', label: 'Members', href: settingsRoutes.members, icon: UsersIcon },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    itemList: [
      { id: 'balance', label: 'Balance', href: settingsRoutes.balance, icon: WalletIcon },
      {
        id: 'payment-methods',
        label: 'Payment Methods',
        href: settingsRoutes.paymentMethods,
        icon: LandmarkIcon,
      },
      {
        id: 'subscription',
        label: 'Subscription',
        href: settingsRoutes.subscription,
        icon: CreditCardIcon,
      },
      { id: 'invoices', label: 'Invoices', href: settingsRoutes.invoices, icon: ReceiptIcon },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    itemList: [
      {
        id: 'connectors',
        label: 'Connectors',
        href: settingsRoutes.connectors,
        icon: PlugIcon,
      },
    ],
  },
]

const VIEW_COMPONENTS: Partial<Record<OrgSettingsViewType, React.FC>> = {
  general: GeneralSettingsView,
  members: MembersSettingsView,
  balance: BalanceSettingsView,
  'payment-methods': PaymentMethodsSettingsView,
  subscription: SubscriptionSettingsView,
  invoices: InvoicesSettingsView,
  connectors: ConnectorsSettingsView,
}

const DefaultContent: React.FC<{ title: string }> = ({ title }) => (
  <div className="org-settings-content">
    <p className="text-muted-foreground">Content for {title} will be added here.</p>
  </div>
)

export const OrgSettingsPage = (): ReactNode => {
  const { settingsView, orgSlug } = useParams<{ settingsView?: string; orgSlug: string }>()
  const navigate = useNavigate()

  const viewType: OrgSettingsViewType =
    ORG_SETTINGS_URL_PATH_TO_VIEW[settingsView ?? ''] ?? ORG_SETTINGS_DEFAULT_VIEW

  useEffect(() => {
    if (!settingsView && orgSlug) {
      navigate(
        `/${orgSlug}/${orgRoutes.settings}/${ORG_SETTINGS_VIEW_TO_URL_PATH[ORG_SETTINGS_DEFAULT_VIEW]}`,
        { replace: true }
      )
    }
  }, [settingsView, orgSlug, navigate])

  const handleNavigate = useCallback(
    (item: SettingsNavItem): void => {
      if (!orgSlug) return
      navigate(`/${orgSlug}/${orgRoutes.settings}/${item.href}`)
    },
    [navigate, orgSlug]
  )

  const contentTitle = ORG_SETTINGS_VIEW_TITLES[viewType]
  const ViewComponent = VIEW_COMPONENTS[viewType]

  return (
    <SettingsLayout
      groupList={ORG_SETTINGS_NAV_GROUPS}
      activeItemId={viewType}
      onNavigate={handleNavigate}
      contentTitle={contentTitle}
      contentDescription={ORG_SETTINGS_VIEW_DESCRIPTIONS[viewType]}
    >
      {ViewComponent ? <ViewComponent /> : <DefaultContent title={contentTitle} />}
    </SettingsLayout>
  )
}
